import { useState, useMemo, useCallback } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { theme, Button, Icon, Text } from '@socrait/react';

import { AccordionBase, AccordionState } from '~/components/Accordion';
import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { useReviewDataLog } from '~/services/review';
import { getMeetingContentState } from '~/pages/review/utils/meetingContentState';

import { BaseActionItemProps } from './ActionItemTypes';

export const DataLogActionItem = ({ date, title, periodId, meetingId }: BaseActionItemProps & { periodId: string }) => {
  const { data, status, refetch, error } = useReviewDataLog({ date, periodId });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [accordionState, setAccordionState] = useState<AccordionState>('initial');

  const contentState = getMeetingContentState(data?.meetings, meetingId, data?.status);
  const studentEvents = contentState.kind === 'content' ? contentState.meeting.studentEvents : undefined;

  // Format the data log entries
  const formattedDataLog = useMemo(() => {
    if (!studentEvents) return '';

    let formatted = '';

    Object.entries(studentEvents).forEach(([studentName, entries]) => {
      formatted += `${studentName}\n\n`;

      entries.forEach((entry) => {
        formatted += `${entry.indication}: ${entry.context}\n`;
      });

      formatted += '\n';
    });

    return formatted;
  }, [studentEvents]);

  useMemo(() => {
    if (status === 'pending') {
      setAccordionState('loading');
    } else if (status === 'success' && data) {
      setAccordionState('loaded');
    } else {
      setAccordionState('initial');
    }
  }, [status, data]);

  const handleViewClick = useCallback(() => {
    // Only refetch if data hasn't been loaded yet
    if (status !== 'success' || !data) {
      refetch();
    }
    setIsCollapsed(false);
    setAccordionState(data ? 'loaded' : 'loading');
  }, [data, refetch, setIsCollapsed, setAccordionState, status]);

  const handleToggleCollapse = useCallback((newIsCollapsed: boolean) => {
    setIsCollapsed(newIsCollapsed);
  }, []);

  const handleReviewButtonClick = useCallback(() => {
    if (studentEvents) {
      setIsCollapsed(false);
      setAccordionState('expanded');
    } else {
      handleViewClick();
    }
  }, [studentEvents, handleViewClick, setIsCollapsed, setAccordionState]);

  const viewButton = (
    <Button
      name="View"
      onPress={handleViewClick}
      containerStyle={styles.viewButton}
      fontStyle={styles.viewButtonText}
    />
  );

  const content = error ? (
    <ErrorBox text="Could not load data log. Please try reopening this action item or reloading the window." />
  ) : (
    <>
      {accordionState === 'loading' ? (
        <View style={styles.loadingContainer}>
          <Loading backgroundColor={theme.colors.transparent} />
        </View>
      ) : (
        <ScrollView>
          {studentEvents ? (
            Object.entries(studentEvents).map(([studentName, entries]) => (
              <View key={studentName} style={styles.studentSection}>
                <Text style={styles.studentName}>{studentName}</Text>
                {entries.map((entry, entryIndex) => (
                  <View key={`${studentName}-${entryIndex}`} style={styles.entryContainer}>
                    <Text style={styles.entryText}>
                      [{entry.indication}] {entry.context}
                    </Text>
                  </View>
                ))}
              </View>
            ))
          ) : (
            <Text style={styles.noDataText}>No data available</Text>
          )}
        </ScrollView>
      )}
    </>
  );

  return (
    <AccordionBase
      title={title}
      icon={<Icon type="log" width={24 - theme.spacing.xsmall / 2} />}
      rightControls={viewButton}
      content={content}
      copyText={formattedDataLog}
      isCollapsed={isCollapsed}
      onToggleCollapse={handleToggleCollapse}
      state={accordionState}
      autoExpand={true}
      onReviewButtonClick={handleReviewButtonClick}
      noContentBorder={!!error}
    />
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    height: 400,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  studentName: {
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.spacing.xsmall,
  },
  entryText: {
    fontSize: theme.fontSizes.small,
  },
  entryContainer: {
    marginBottom: theme.spacing.xsmall,
  },
  noDataText: {
    color: theme.colors.darkGray,
    textAlign: 'center',
    padding: theme.spacing.large,
  },
  studentSection: {
    padding: theme.spacing.medium,
  },
  viewButton: {
    borderRadius: theme.borderRadius.xlarge,
  },
  viewButtonText: {
    color: theme.colors.primaryPurple,
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
    fontFamily: theme.fonts.medium,
  },
});
